import {Plural, Trans} from '@lingui/macro';
import React, {FC, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {AppRoutesPrefixesEnum} from '../../../../../../../../../src/components/appRoutesPrefixes';
import {CustomFieldModel} from '../../../../../../../../../src/models/api/customFieldModel';
import {SettingsCustomFieldsPagesEnum} from '../../../../../../../../../src/models/url/settingsUrlModel';
import {getCustomFieldsBaseUrl} from '../../../../../../../../../src/settings/customFields/customFieldsHelpers';
import {ResourceMap} from '../../../../../../../../../src/store/appTypes';
import {
  generateNavigationBlockerId,
  NavigationBlockerId,
} from '../../../../../../../../../src/store/panels/app/appPanelHelpers';
import {
  AccountsCsvStandardFieldsEnum,
  ContactsManagerAccountsCsvFileState,
  FileMetadata,
} from '../../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileState';
import {ContactsManagerPanelId} from '../../../../../../../../../src/store/panels/panelStateBase';
import {useNavigationBlocking} from '../../../../../../../../../srcCommon/app/navigationBlocking';
import {Handler, HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../../shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../../shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {InfoButton} from '../../../common/buttons/infoButton';
import {DragAndDrop} from '../../../common/dragAndDrop/dragAndDrop';
import {
  DragAndDropFileTypesEnum,
  DragAndDropVersionsEnum,
} from '../../../common/dragAndDrop/dragAndDropConstants';
import {PageWrapper} from '../../../common/pageWrapper';
import {ContactsManagerHeader} from '../../../contactsManagerHeader';
import {buildInfoBoxToggleLabel} from '../../../helpers/infoBoxHelpers';
import {AccountsCsvImportInfoBoxes} from './accountsCsvImportInfoBoxes';
import {AccountsCsvImportMapping} from './accountsCsvImportMapping/accountsCsvImportMapping';
import {AccountsCsvImportMappingIncompleteDialog} from './accountsCsvImportMappingIncompleteDialog';
import {AccountsCsvImportStepHeader} from './accountsCsvImportStepHeader';
import {AccountsCsvImportUnsavedChangesDialog} from './accountsCsvImportUnsavedChangesDialog';
import {FileErrorWarningBox} from './accountsCsvImportWarningErrorBox/fileErrorWarningBox';

/*
 * Constants.
 */

enum LinkedPagesEnum {
  ACCOUNT_CUSTOM_FIELDS = 'account_custom_fields',
}

const standardFieldsOrder = [
  AccountsCsvStandardFieldsEnum.name,
  AccountsCsvStandardFieldsEnum.description,
  AccountsCsvStandardFieldsEnum.domains,
  AccountsCsvStandardFieldsEnum.externalid,
] as string[];

/*
 * Props.
 */

export interface AccountsCsvImportProps {
  hasAccountMappedCsvImport: boolean;
  isInfoBoxExpanded: boolean;
  isUserGlobalAdmin: boolean;
  fileState?: ContactsManagerAccountsCsvFileState;
  customFieldsForAccounts: ResourceMap<CustomFieldModel>;
  panelId: ContactsManagerPanelId;
  navigateToLink: HandlerOf<string>;
  onClearFile: Handler;
  onSetMapping: HandlerOf<Record<string, number>>;
  onToggleInfoBox: Handler;
  onUploadAccountsCSV: (file: File, dryRun?: boolean) => void;
  generateWarningFileAsync: (
    file: File,
    mapping: Record<string, number>,
  ) => Promise<File | undefined> | undefined;
  onImportAccounts: (file: File, ignoreNavigationBlockerId: NavigationBlockerId) => void;
}

/*
 * Styles.
 */

const StyledUploadCSVStepWrapperDiv = styled.div`
  padding-top: 15px;
  padding-bottom: 41px;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/*
 * Component.
 */

export const AccountsCsvImport: FC<AccountsCsvImportProps> = ({
  hasAccountMappedCsvImport,
  isInfoBoxExpanded,
  isUserGlobalAdmin,
  fileState,
  customFieldsForAccounts,
  panelId,
  navigateToLink,
  onClearFile,
  onSetMapping,
  onToggleInfoBox,
  onUploadAccountsCSV,
  onImportAccounts,
  generateWarningFileAsync,
}) => {
  const [isShowingMappingIncompleteDialog, setIsShowingMappingIncompleteDialog] = useState<boolean>(false);
  const [navigatingToLinkedPage, setNavigatingToLinkedPage] = useState<LinkedPagesEnum | undefined>(
    undefined,
  );
  // generate our own id so we do not block navigation when you click import
  const navigationBlockerId = useMemo(() => generateNavigationBlockerId(), []);
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(
    panelId,
    Boolean(fileState?.attachment),
    navigationBlockerId,
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const mapping = fileState?.mapping || {};
  const fileMetadata = fileState?.fileMetadata;
  const headers = fileMetadata?.headers || [];
  const standardHeaders = filterStandardHeaders(headers);
  const customHeaders = filterCustomHeaders(headers, standardHeaders);

  const totalUnmappedHeaders = countUnmappedHeaders(headers, mapping);

  const totalInvalidValues = headers.reduce<number>((sum, header) => {
    if (checkIsStandardHeader(header)) {
      return sum + (fileMetadata?.invalidStandardFieldCounts[header] || 0);
    }
    return sum + computeCustomFieldInvalidCount(header, mapping, customFieldsForAccounts, fileMetadata);
  }, 0);

  const navigateToAccountsPage = () => {
    navigateToLink(`/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`);
  };

  const navigateToAccountCustomFieldsPage = () => {
    navigateToLink(`${getCustomFieldsBaseUrl(SettingsCustomFieldsPagesEnum.ACCOUNTS)}/overview`);
  };

  const onNavigateToAccountCustomFieldsPage = () => {
    setNavigatingToLinkedPage(LinkedPagesEnum.ACCOUNT_CUSTOM_FIELDS);
    navigateToAccountCustomFieldsPage();
  };

  if (!hasAccountMappedCsvImport) {
    navigateToAccountsPage();
  }

  const renderWarningMessage = (header: string) => {
    const invalidCount = computeCustomFieldInvalidCount(
      header,
      mapping,
      customFieldsForAccounts,
      fileMetadata,
    );
    if (invalidCount !== 0) {
      return (
        <Plural
          id="account-csv-import-mapping-custom-field-warning"
          value={invalidCount}
          one="# value in your CSV does not match the selected Front field type"
          other="# values in your CSV do not match the selected Front field type"
        />
      );
    }
    return null;
  };

  const onImportClick = () => {
    if (!fileState?.file) {
      return;
    }
    if (totalUnmappedHeaders > 0 || totalInvalidValues > 0) {
      setIsShowingMappingIncompleteDialog(true);
    } else {
      onImportAccounts(fileState.file as File, navigationBlockerId);
    }
  };

  return (
    <>
      <PageWrapper
        outerRef={scrollRef}
        header={renderHeader(navigateToAccountsPage, onToggleInfoBox, isInfoBoxExpanded)}
        footer={renderFooter(navigateToAccountsPage, onImportClick, isImportButtonDisabled(fileState))}
        sideContent={isInfoBoxExpanded && <AccountsCsvImportInfoBoxes />}
      >
        <AccountsCsvImportStepHeader
          title={<Trans id="accounts-csv-import-upload-step-header">Upload CSV</Trans>}
          stepNumber={1}
        />
        <StyledUploadCSVStepWrapperDiv>
          <DragAndDrop
            attachment={fileState?.attachment}
            onDropFile={(file) => onUploadAccountsCSV(file, true)}
            onClearFile={onClearFile}
            allowedFileTypes={[DragAndDropFileTypesEnum.CSV]}
            isUploading={fileState?.attachment.uploadStatus === AsyncStatusesEnum.LOADING}
            hasError={fileState?.attachment.uploadStatus === AsyncStatusesEnum.FAILED}
            version={DragAndDropVersionsEnum.V2}
          />
          <FileErrorWarningBox
            invalidCount={totalInvalidValues}
            fileErrors={fileState?.errors}
            fileName={fileState?.attachment.name}
            fileSize={fileState?.attachment.size}
            generateWarningFileAsync={async () => generateWarningFileAsync(fileState?.file as File, mapping)}
          />
        </StyledUploadCSVStepWrapperDiv>
        <AccountsCsvImportStepHeader
          title={<Trans id="accounts-csv-import-mapping-step-header">Map CSV headers to Front fields</Trans>}
          stepNumber={2}
          isDisabled={isFileNotLoaded(fileState)}
          description={
            customHeaders.length > 0 ? (
              <Trans id="accounts-csv-import-mapping-step-description-custom-headers">
                Each of your CSV column headers will become an account field in Front. In the table below,
                create or select Front account fields to map to your CSV columns headers.
              </Trans>
            ) : (
              <Trans id="accounts-csv-import-mapping-step-description-no-custom-headers">
                Each of your CSV column headers will become an account field in Front.
              </Trans>
            )
          }
        />
        <AccountsCsvImportMapping
          standardHeaders={standardHeaders}
          customHeaders={customHeaders}
          mapping={mapping}
          onWarningIconClick={() => {
            scrollRef.current?.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
          isShowingAdminError={isShowingAdminError(customHeaders, customFieldsForAccounts, isUserGlobalAdmin)}
          onMappingChange={onSetMapping}
          isLoading={isMappingStepLoading(fileState)}
          isDisabled={isFileNotLoaded(fileState)}
          renderWarningMessage={renderWarningMessage}
          onNavigateToLink={onNavigateToAccountCustomFieldsPage}
        />
      </PageWrapper>
      {isShowingMappingIncompleteDialog && (
        <AccountsCsvImportMappingIncompleteDialog
          onClose={() => setIsShowingMappingIncompleteDialog(false)}
          onImport={() => {
            if (fileState?.file) {
              onImportAccounts(fileState.file as File, navigationBlockerId);
            }
          }}
          totalInvalidValues={totalInvalidValues}
          totalUnmappedHeaders={totalUnmappedHeaders}
        />
      )}
      {didBlockNavigation && (
        <AccountsCsvImportUnsavedChangesDialog
          onClose={() => {
            setNavigatingToLinkedPage(undefined);
            cancelNavigation();
          }}
          onDiscard={() => {
            onClearFile();
            unblockNavigation();
          }}
          message={renderUnsavedChangesDialogMessage(navigatingToLinkedPage)}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderFooter(onCancelClick: Handler, onImportClick: Handler, isDisabled: boolean) {
  return (
    <ButtonGroup>
      <Button isRounded onClick={onCancelClick}>
        <Trans id="accounts-csv-import-cancel-button">Cancel</Trans>
      </Button>
      <Button isRounded isDisabled={isDisabled} variant={ButtonVariantsEnum.PRIMARY} onClick={onImportClick}>
        <Trans id="accounts-csv-import-button">Import accounts</Trans>
      </Button>
    </ButtonGroup>
  );
}

function renderHeader(navigateToAccountsPage: Handler, onToggleInfoBox: Handler, isInfoBoxExpanded: boolean) {
  return (
    <ContactsManagerHeader
      title={<Trans id="contacts-manager-accounts-csv-import-title">Import accounts from a CSV file</Trans>}
      topLevelBreadcrumb={{
        label: <Trans id="contacts-manager-accounts-csv-import-breadcrumb">Accounts</Trans>,
        onBreadcrumbClick: navigateToAccountsPage,
      }}
      interactionId="contacts-manager-accounts-csv-import-header"
      actions={
        <InfoButton
          label={buildInfoBoxToggleLabel()}
          onToggle={onToggleInfoBox}
          isActive={isInfoBoxExpanded}
        />
      }
    />
  );
}

// Non-admins cannot create custom fields so we show an error if custom headers
// are present in the csv but there are no custom fields to map them to
function isShowingAdminError(
  customHeaders: string[],
  customFieldsForAccounts: ResourceMap<CustomFieldModel>,
  isUserGlobalAdmin: boolean,
) {
  return customHeaders.length > 0 && Object.keys(customFieldsForAccounts).length === 0 && !isUserGlobalAdmin;
}
function isMappingStepLoading(fileState?: ContactsManagerAccountsCsvFileState) {
  // if the file is uploaded but we haven't fetched the metadata yet
  return fileState?.attachment.uploadStatus === AsyncStatusesEnum.LOADED && !fileState.fileMetadata;
}

function isFileNotLoaded(fileState?: ContactsManagerAccountsCsvFileState) {
  return fileState?.attachment.uploadStatus !== AsyncStatusesEnum.LOADED;
}

function isImportButtonDisabled(fileState?: ContactsManagerAccountsCsvFileState) {
  return !fileState?.attachment || isFileNotLoaded(fileState) || isMappingStepLoading(fileState);
}

function countUnmappedHeaders(headers: string[], mapping: Record<string, number>) {
  const unmappedHeaders = headers.filter((header) => !checkIsStandardHeader(header) && !mapping[header]);
  return unmappedHeaders.length;
}

function checkIsStandardHeader(header: string) {
  return header.toLowerCase() in AccountsCsvStandardFieldsEnum;
}

// filter & sort headers so standard headers are in order (Name, Description, etc.)
function filterStandardHeaders(headers: string[]) {
  const standardFieldHeaders = headers
    .filter(checkIsStandardHeader)
    .sort(
      (a, b) => standardFieldsOrder.indexOf(a.toLowerCase()) - standardFieldsOrder.indexOf(b.toLowerCase()),
    );

  return standardFieldHeaders;
}

function filterCustomHeaders(headers: string[], standardHeaders: string[]) {
  const customFieldHeaders = headers.filter((header) => !standardHeaders.includes(header));
  return customFieldHeaders;
}

function computeCustomFieldInvalidCount(
  header: string,
  mapping: Record<string, number>,
  customFields: ResourceMap<CustomFieldModel>,
  fileMetadata?: FileMetadata,
) {
  const customFieldId = mapping[header];
  const customField = customFields[customFieldId];
  const invalidCountsByType = fileMetadata?.invalidCustomFieldCounts[header];
  if (!customField || !invalidCountsByType) {
    return 0;
  }
  const invalidCustomFieldCount = invalidCountsByType[customField.type];
  if (invalidCustomFieldCount) {
    return invalidCustomFieldCount;
  }
  return 0;
}

function renderUnsavedChangesDialogMessage(navigatingToLinkedPage?: LinkedPagesEnum) {
  if (navigatingToLinkedPage === LinkedPagesEnum.ACCOUNT_CUSTOM_FIELDS) {
    return (
      <Trans id="accounts-csv-import-unsaved-changes-accounts-custom-fields">
        Would you like to discard changes and go to the Account custom fields page?
      </Trans>
    );
  }
  return <Trans id="accounts-csv-import-unsaved-changes">Would you like to discard changes?</Trans>;
}
